import { z } from 'zod';
import { invitationIssuedSchema } from './invitation.js';
import { planKeySchema } from './features.js';
import { paginationQuerySchema, paginated } from './common.js';
/**
 * Tenant contracts.
 *
 * Request schemas are `.strict()`: a client cannot submit server-owned fields
 * (id, status, createdAt, deletedAt) even accidentally. Status is never set by
 * a PATCH — it changes only through the explicit suspend/reactivate verbs, so
 * a lifecycle transition is always a deliberate, separately-audited action.
 */
// Mirrors TENANT_STATUSES in @oip/domain and the Tenant_status_check constraint.
export const tenantStatusSchema = z.enum(['ACTIVE', 'SUSPENDED']);
/**
 * The deployment regions, and how each one is written on a screen.
 *
 * Region is a free string in the schema and anticipates data residency
 * (ADR 0009). Constrained here to the regions actually in use; widening is a
 * contract change, which is the point.
 *
 * ⚠️ **ONE vocabulary, because there were FIVE — 18 September 2026.** The enum was here and
 * the list was ALSO hand-written in `TenantListPage`, `TenantCreatePage`, `TenantEditPage`
 * and `CreateCompanyDialog`, with the labels written out three times on top. Adding a region
 * meant finding all of them, and a screen that missed the edit offers a narrower set with
 * nothing failing — the server accepts what the dropdown never shows. Every one of those
 * reads this now, and `tenantRegionSchema` is DERIVED from it, so a new entry cannot reach
 * the API without reaching the screens.
 *
 * ⚠️ **`WW` is not a place, and that is deliberate.** It means the customer has not asked for
 * residency in any one of them — which is a real and common answer, and materially different
 * from picking a region at random to fill the field in. Nothing in the platform ROUTES on
 * region today (it is stored and filtered, never decided upon), so this adds a label rather
 * than a behaviour; the day something does route, `WW` is the value that needs a rule.
 */
export const TENANT_REGIONS = [
    { value: 'US', label: 'United States', suffix: 'US' },
    { value: 'UK', label: 'United Kingdom', suffix: 'UK' },
    { value: 'IN', label: 'India', suffix: 'IN' },
    { value: 'WW', label: 'Worldwide', suffix: 'WW' },
];
/**
 * `United Kingdom` — the name alone, for a cell or a sentence.
 *
 * ⚠️ Falls through to the stored value, never to a placeholder. `region` is a free string
 * with no database constraint, so a tenant carrying one this build has not caught up with is
 * real — and ugly beats "Unknown" on a row that has one. The `policyLabel` rule.
 */
export function tenantRegionLabel(value) {
    return TENANT_REGIONS.find((r) => r.value === value)?.label ?? value;
}
/**
 * `United Kingdom (UK)` — the name WITH its code, for a picker.
 *
 * ⚠️ TWO forms deliberately, and the difference is whether a tooltip is available. A table
 * cell shows the name and carries the code on hover and in its export, so appending it there
 * would state one fact twice and widen the column for nothing. A `select` has nowhere to put
 * a tooltip, and the code is what an operator filing a ticket or calling the API needs.
 */
export function tenantRegionOptionLabel(value) {
    const found = TENANT_REGIONS.find((r) => r.value === value);
    return found ? `${found.label} (${found.suffix})` : value;
}
export const TENANT_REGION_VALUES = TENANT_REGIONS.map((r) => r.value);
export const tenantRegionSchema = z.enum(TENANT_REGIONS.map((r) => r.value));
// ── platform-scoped ─────────────────────────────────────────────────────────
/**
 * Email is normalised to lower-case at the contract boundary so that
 * `User.email @unique` behaves case-insensitively and a second identity cannot
 * be created for the same person by varying capitalisation.
 */
export const normalisedEmailSchema = z.string().trim().toLowerCase().email().max(254);
/**
 * Optional initial tenant administrator.
 *
 * Note what is ABSENT: there is no `role` field. The initial administrator is
 * always TENANT_ADMIN, fixed server-side. Accepting a role here would let a
 * client choose the privilege level attached to a new tenant, which is exactly
 * the untrusted-privilege-information case the design forbids. `.strict()`
 * turns any attempt into a 400 rather than a silently ignored field.
 */
export const initialTenantAdminSchema = z
    .object({
    email: normalisedEmailSchema,
    /** Used only when the user does not already exist. */
    displayName: z.string().trim().min(1).max(120).optional(),
})
    .strict();
export const createTenantRequestSchema = z
    .object({
    name: z.string().trim().min(2).max(120),
    region: tenantRegionSchema.default('US'),
    llmEnabled: z.boolean().default(false),
    /**
     * Grants the plan's features on creation, with `source = PLAN`.
     *
     * Optional, and omitting it creates a tenant with NO entitlements — which
     * is a legitimate state (provision now, sell later) rather than an
     * oversight. The features card is where it is corrected.
     */
    plan: planKeySchema.optional(),
    initialAdmin: initialTenantAdminSchema.optional(),
})
    .strict();
export const updateTenantRequestSchema = z
    .object({
    name: z.string().trim().min(2).max(120).optional(),
    region: tenantRegionSchema.optional(),
    llmEnabled: z.boolean().optional(),
    /**
     * Changing the plan re-syncs PLAN-sourced features to match it.
     *
     * TRIAL and OVERRIDE rows are left alone: those were deliberate exceptions
     * to a plan, and silently discarding them on an unrelated edit would undo
     * somebody's decision without telling them.
     */
    plan: planKeySchema.optional(),
    /**
     * How many sub-tenants this customer may have (D16).
     *
     * Platform-only, because it is a blast radius rather than a licence: it
     * bounds the consolidated fan-out, and a parent with two hundred children
     * makes every aggregate a scan. Raising it is a deliberate operator act.
     */
    maxSubTenants: z.number().int().min(1).max(500).optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'At least one field must be provided' });
/** Suspension/reactivation carry an optional operator note for the audit trail. */
export const tenantLifecycleRequestSchema = z
    .object({ note: z.string().trim().min(1).max(500).optional() })
    .strict();
export const tenantListQuerySchema = paginationQuerySchema
    .extend({
    sort: z.enum(['name', 'createdAt', 'status']).default('name'),
    direction: z.enum(['asc', 'desc']).default('asc'),
    status: tenantStatusSchema.optional(),
    region: tenantRegionSchema.optional(),
    search: z.string().trim().min(1).optional(),
})
    .strict();
export const tenantListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    region: z.string(),
    status: z.string(),
    /**
     * PARENT or SUB.
     *
     * On the LIST as well as the detail, because a caller cannot otherwise tell
     * an organisation from a branch without opening every row — and the two
     * behave differently enough that it matters: a PARENT holds no vehicles,
     * incidents or telemetry at all (D2), so entering one lands on screens that
     * are empty by construction. `tenantDetailSchema` extends this, so it now
     * inherits the field rather than declaring its own.
     */
    tenantType: z.string(),
    llmEnabled: z.boolean(),
    /** A key from PLANS, or null for a tenant that pre-dates plans. */
    plan: planKeySchema.nullable(),
    createdAt: z.string(),
});
export const tenantListResponseSchema = paginated(tenantListItemSchema);
/**
 * Detail adds counts a platform operator needs to judge a tenant before acting
 * on it (chiefly: does suspending this cut off a live fleet?).
 */
/**
 * Who sold this tenant, and who manages it day to day.
 *
 * ⚠️ **A SYSTEM partner or reseller is reported as NULL, never by name.** Every tenant that
 * came to OIP directly is attached to the Default pair by `20260915000092`, and naming that
 * bootstrap row to a reader is a mistake this platform has already made once — a direct
 * customer was told *"Platform Partner, who provides this service, is suspended"*, about an
 * internal row they have never heard of. Null here means "came to us directly", which is a
 * FACT rather than missing data, and the screen says so in those words.
 */
export const tenantCommercialChainSchema = z.object({
    partnerId: z.string().uuid().nullable(),
    partnerName: z.string().nullable(),
    resellerId: z.string().uuid().nullable(),
    resellerName: z.string().nullable(),
});
export const tenantDetailSchema = tenantListItemSchema.extend({
    updatedAt: z.string(),
    /**
     * ⚠️ Carried so the tenant list and the partner list stop reading as two copies of one
     * thing. They answer different questions — "every customer and what state each is in"
     * against "the resale arrangements" — and a tenant that appears on both with no visible
     * connection is what makes the overlap look like duplication rather than two views.
     */
    chain: tenantCommercialChainSchema,
    counts: z.object({
        activeMembers: z.number().int(),
        vehicles: z.number().int(),
    }),
});
/**
 * Result of provisioning a tenant.
 *
 * `temporaryPassword` is returned EXACTLY ONCE, in the create response, and
 * only when the administrator account was newly created. It is never stored in
 * plaintext, never written to an audit snapshot (the audit writer rejects
 * password-like keys outright), and never retrievable again — a lost one is
 * replaced by a password reset, not by re-reading it.
 *
 * When the administrator already existed, `created` is false and
 * `temporaryPassword` is null: an existing identity is reused rather than
 * duplicated, and its credentials are untouched.
 */
export const initialTenantAdminResultSchema = z.object({
    userId: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    created: z.boolean(),
    temporaryPassword: z.string().nullable(),
});
export const tenantProvisioningResponseSchema = tenantDetailSchema.extend({
    initialAdmin: initialTenantAdminResultSchema.nullable(),
});
// ── tenant-scoped (existing) ────────────────────────────────────────────────
export const currentTenantResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    region: z.string(),
    status: z.string(),
    llmEnabled: z.boolean(),
});
/**
 * A tenant administrator may correct their own tenant's display name only.
 * Region is a data-residency concern and llmEnabled is a commercial one — both
 * are platform decisions, so neither is exposed here.
 */
export const updateCurrentTenantRequestSchema = z
    .object({ name: z.string().trim().min(2).max(120) })
    .strict();
export const tenantSettingsResponseSchema = z.object({
    tenantId: z.string().uuid(),
    settings: z.array(z.object({ key: z.string(), value: z.string() })),
});
// ── sub-tenants (tenant-hierarchy-design D10, D16, D18) ─────────────────────
/**
 * Sub-tenant management, from the PARENT context.
 *
 * These routes exist only on a parent. That is not a convention: `PARENT_CHILD_ADMIN`
 * — the role a parent administrator holds *inside* a child — never has a parent
 * context, so "cannot create sub-tenants from a child context" (D3) is enforced by
 * where the route lives rather than by a check somebody has to remember.
 */
export const subTenantSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** True for the sub-tenant a customer has always had; it cannot be created or renamed away. */
    isDefault: z.boolean(),
    status: tenantStatusSchema,
    /** Soft-archived. The row and its history remain; its vehicles were archived with it. */
    archived: z.boolean(),
    /** Optional (D1). Tenant name is the fallback sort. */
    displayOrder: z.number().int().nullable(),
    createdAt: z.string(),
    counts: z.object({
        activeMembers: z.number().int(),
        vehicles: z.number().int(),
        /**
         * Archived vehicles, which `vehicles` excludes.
         *
         * Reported because restoring an archived branch brings back its vehicles
         * ARCHIVED — deliberately, since some were retired before the branch was
         * and nothing records which. Without this the row reads "0 vehicles" and
         * the fleet that is still there is invisible until somebody thinks to try
         * "Include archived" on a page they have no reason to open.
         */
        archivedVehicles: z.number().int(),
    }),
});
export const subTenantListQuerySchema = z
    .object({
    /**
     * `z.enum` rather than `z.coerce.boolean()`: coercion makes the STRING
     * "false" truthy, which is R8 in the known-issues list and not a mistake
     * worth repeating.
     */
    includeArchived: z.enum(['true', 'false']).default('false'),
})
    .strict();
export const subTenantListResponseSchema = z.object({
    items: z.array(subTenantSchema),
    /** The cap from the parent row (D16) — a blast radius, not a licence. */
    maxSubTenants: z.number().int(),
});
/**
 * `initialAdmin` is REQUIRED, unlike on a platform tenant (D18).
 *
 * A sub-tenant nobody can administer is unreachable from the moment it exists —
 * the same mistake as a scheduled report with no recipients. A platform tenant may
 * legitimately be provisioned before anyone is nominated, because a platform
 * administrator can always enter it; nobody can enter an empty sub-tenant.
 *
 * There is deliberately no `role` field, for the same reason as the platform
 * version: the initial administrator is always TENANT_ADMIN, fixed server-side.
 */
export const createSubTenantRequestSchema = z
    .object({
    name: z.string().trim().min(2).max(120),
    displayOrder: z.number().int().min(0).max(9999).optional(),
    /**
     * Send the initial administrator an INVITATION rather than a one-time password.
     *
     * ⚠️ Optional and absent-means-false, for the reason the company create already records:
     * every existing caller — the dialog, the seeds, the tests — would otherwise have to state
     * `invite: false` to say nothing had changed.
     *
     * ⚠️ **It exists for the IMPORT more than for the dialog.** One fleet account created on
     * screen shows its password once and that works; a file creating forty mints forty
     * plaintext passwords with nowhere to go, which is the problem §0.2 named and the
     * invitation flow was built to end.
     */
    invite: z.boolean().optional(),
    initialAdmin: initialTenantAdminSchema,
})
    .strict();
export const updateSubTenantRequestSchema = z
    .object({
    name: z.string().trim().min(2).max(120).optional(),
    displayOrder: z.number().int().min(0).max(9999).nullable().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'At least one field must be provided' });
export const createSubTenantResponseSchema = subTenantSchema.extend({
    /**
     * The invitation, when one was issued.
     *
     * ⚠️ `NOT_CONFIGURED` is the one that matters — no mail server, so `acceptUrl` comes back
     * and the caller MUST surface it, or the fleet account has an administrator nobody can ever
     * reach. That degradation is exactly why a bulk import has to check deliverability ONCE
     * before the file rather than per row.
     */
    invitation: invitationIssuedSchema.nullable().default(null),
    initialAdmin: initialTenantAdminResultSchema,
});
/**
 * Suspend, reactivate or archive a sub-tenant (design D19).
 *
 * Carries only an optional note, like the platform tenant verbs it mirrors. The
 * transition is in the URL rather than the body so that a lifecycle change is
 * always a deliberate, separately-audited action — the same reason
 * `updateTenantRequestSchema` has no `status` field.
 */
export const subTenantLifecycleRequestSchema = z
    .object({ note: z.string().trim().min(1).max(500).optional() })
    .strict();
//# sourceMappingURL=tenant.js.map